// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { Button, ImprTooltip, Modal } from '@imprivata-cloud/components';
import { Col, Form, Row } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { idpGet } from '../../../api/commonServices';
import {
  getIdpConfigSAML,
  idpSave,
  registerPortalDomain,
  requestPortalDomainVerification,
  updateIdpConfigSAML,
} from '../../../api/portalServices';
import type { IdpConfigGetResponse, IdpMetadataInfo } from '../../../api/types';
import EditIcon from '../../../assets/icons/edit.svg?react';
import { useSession } from '../../../common/authorization/context/Session';
import { withRole } from '../../../common/authorization/Roles';
import IdpInfoForm, {
  type FormValues,
  IdpFormFieldNames,
} from '../../../components/idpInfoForm/IdpInfoForm';
import { ErrorCode } from '../../../errorHandler/constants';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '../../../errorHandler/errors';
import ConfirmOwnershipModal from '../../../components/otpChallenge/ConfirmOwnershipModal';
import { featureSupported } from '../../../featureFlagsUtils';
import { getSPMetadataURL } from '../../../utils/utils';
import { ROLE_TYPE_ADMIN } from '../../constants';
import './Integrations.less';

async function requestChallengeOtp(
  domainName: string,
  adminEmail: string,
): Promise<{ otpId: string }> {
  const { otpId } = await requestPortalDomainVerification({ domainName, email: adminEmail });
  if (!otpId) {
    throw new Error('Domain verification status changed during challenge');
  }
  return { otpId };
}

function remapAlreadyExists(error: ApiError): ApiError {
  if (error.code === ErrorCode.ALREADY_EXISTS) {
    error.code = ErrorCode.DOMAIN_ALREADY_EXISTS;
  }
  return error;
}

type DomainChallenge =
  | { kind: 'idle' }
  | { kind: 'challenging'; domain: string; adminEmail: string; otpId: string; issuedAtMs: number }
  | { kind: 'deferred'; domain: string; adminEmail: string; otpId: string; issuedAtMs: number };

const IDLE: DomainChallenge = { kind: 'idle' };

const SamlIdp = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { emitError, clearErrorNotifications, emitSuccess } = useErrorContext();
  const [idpMetadataInfoList, setIdpMetadataInfoList] = useState<IdpMetadataInfo[]>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [initialSamlConfig, setInitialSamlConfig] = useState<IdpConfigGetResponse>();
  const [idpForm] = Form.useForm<FormValues>();
  const { tenantId } = useSession();
  const [challenge, setChallenge] = useState<DomainChallenge>(IDLE);
  const [savedDomain, setSavedDomain] = useState<string>();
  const emailVerificationEnabled = featureSupported('EMAIL_VERIFICATION_ENABLED');

  const fetchData = useCallback(async () => {
    try {
      clearErrorNotifications();
      setIsLoading(true);
      const idp = await idpGet();
      setIdpMetadataInfoList(idp?.idpMetadataInfoList);
      const metadata = idp?.idpMetadataInfoList?.[0];
      idpForm.setFieldValue(IdpFormFieldNames.NAME, metadata?.idpDisplayName);
      idpForm.setFieldValue(IdpFormFieldNames.URL, metadata?.metadataUrl);
      idpForm.setFieldValue(IdpFormFieldNames.DOMAIN, idp?.domainNames?.[0]);
      setSavedDomain(idp?.domainNames?.[0]);
      setIsLoading(false);
    } catch (e) {
      console.log(e);
      emitError(new AppError(e as ApiError));
    }
  }, [idpForm.setFieldValue, emitError, clearErrorNotifications]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  async function closeModal() {
    if (idpForm.isFieldsTouched()) {
      await fetchData();
    }
    setIsModalVisible(false);
  }

  async function saveDomainChange(
    formValues: FormValues,
    newDomain: string,
    idpMetadataId: string | undefined,
  ): Promise<void> {
    const metadataUrl = idpMetadataInfoList?.[0]?.metadataUrl || formValues.IdPURL;
    const rawMetadata = formValues.IdPXML || undefined;
    const idpSaveRes = await idpSave({
      metadataId: idpMetadataId,
      displayName: formValues.IdPName,
      domain: newDomain,
      metadata: metadataUrl || rawMetadata ? { metadataUrl, rawMetadata } : undefined,
    });
    await updateSamlGroups(formValues, idpSaveRes.metadataId);
    setSavedDomain(newDomain);
    await closeModal();
  }

  const idpFormOnFinish = async (formValues: FormValues) => {
    const idpMetadataInfo = idpMetadataInfoList?.[0];
    try {
      setIsLoading(true);
      clearErrorNotifications();

      const normalisedDomain = formValues.IdPDomain.toLowerCase().trim();
      const normalisedSaved = (savedDomain ?? '').toLowerCase().trim();

      if (!emailVerificationEnabled || normalisedDomain === normalisedSaved) {
        await saveDomainChange(formValues, normalisedDomain, idpMetadataInfo?.idpMetadataId);
        return;
      }

      const adminEmail = `${formValues.AdminEmailLocalPart}@${normalisedDomain}`;
      const response = await requestPortalDomainVerification({
        domainName: normalisedDomain,
        email: adminEmail,
      });

      if (!response.otpId) {
        await saveDomainChange(formValues, normalisedDomain, idpMetadataInfo?.idpMetadataId);
        emitSuccess(t('portal.settings.integrations.domain-verified-banner'));
        return;
      }

      setChallenge({
        kind: 'challenging',
        domain: normalisedDomain,
        adminEmail,
        otpId: response.otpId,
        issuedAtMs: Date.now(),
      });
    } catch (error) {
      const e = error as ApiError;
      emitError(new AppError(remapAlreadyExists(e)));
    } finally {
      setIsLoading(false);
    }
  };

  const updateSamlGroups = async (values: FormValues, metadataId: string) => {
    const groupsChanged =
      values?.AdminGroupsName !== initialSamlConfig?.samlGroupAttrName ||
      values?.AdminGroupsValues !== initialSamlConfig?.samlGroupAttrValues.join(', ');
    if (groupsChanged) {
      try {
        await updateIdpConfigSAML(
          {
            idpMetadataId: metadataId ?? idpMetadataInfoList?.[0]?.idpMetadataId,
            idpConfigId: initialSamlConfig?.idpConfigId,
            samlGroupAttrName: values.AdminGroupsName || '',
            samlGroupAttrValues: (values.AdminGroupsValues ?? '')
              .split(',')
              .map(v => v.trim())
              .filter(v => v !== ''),
          },
          tenantId || '',
        );
      } catch (e) {
        emitError(new AppError(e as ApiError));
      }
    }
  };

  const handleEditSamlConfig = async () => {
    try {
      const samlConfig = await getIdpConfigSAML(
        { idpMetadataId: idpMetadataInfoList?.[0]?.idpMetadataId || '' },
        tenantId || '',
      );
      idpForm.setFieldValue(IdpFormFieldNames.GROUPS_NAME, samlConfig.samlGroupAttrName);
      idpForm.setFieldValue(
        IdpFormFieldNames.GROUPS_VALUES,
        samlConfig.samlGroupAttrValues.join(', '),
      );
      setInitialSamlConfig(samlConfig);
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
    setIsModalVisible(true);
  };

  const challengeDomain = challenge.kind !== 'idle' ? challenge.domain : null;
  const challengeEmail = challenge.kind !== 'idle' ? challenge.adminEmail : null;
  const effects = useMemo(
    () =>
      challengeDomain !== null && challengeEmail !== null
        ? {
            request: () => requestChallengeOtp(challengeDomain, challengeEmail),
            confirm: (otpId: string, code: string) => registerPortalDomain(challengeDomain, otpId, code),
          }
        : null,
    [challengeDomain, challengeEmail],
  );

  const handleVerified = async () => {
    if (challenge.kind === 'challenging') {
      const formValues = idpForm.getFieldsValue();
      const idpMetadataInfo = idpMetadataInfoList?.[0];
      try {
        setIsLoading(true);
        await saveDomainChange(formValues, challenge.domain, idpMetadataInfo?.idpMetadataId);
        emitSuccess(t('portal.settings.integrations.domain-verified-banner'));
        setChallenge(IDLE);
      } catch (e) {
        emitError(new AppError(remapAlreadyExists(e as ApiError)));
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="integrations-container">
      <Row>
        <Col sm={24} md={24} lg={24} className="setup-content">
          {idpMetadataInfoList?.[0] ? (
            <div className="idp-row">
              <div>{idpMetadataInfoList[0].idpDisplayName}</div>
              <ImprTooltip title={t('portal.settings.integrations.tooltip')}>
                <Button
                  disabled={isLoading}
                  type="text"
                  icon={<EditIcon fill="#1474CE" />}
                  onClick={handleEditSamlConfig}
                  aria-label="edit"
                />
              </ImprTooltip>
            </div>
          ) : (
            <div className="idp-row">
              <Button disabled={isLoading} type="link" onClick={() => setIsModalVisible(true)}>
                {t('portal.settings.integrations.no-idp')}
              </Button>
            </div>
          )}
        </Col>
      </Row>

      <Modal
        title={t('portal.settings.integrations.modal.title', {
          mode: idpMetadataInfoList?.[0]?.idpMetadataId ? t('common.edit') : t('common.add'),
        })}
        open={isModalVisible}
        width={720}
        className="saml-idp-modal"
        wrapClassName="saml-idp-modal-wrap"
        destroyOnHidden
        onCancel={closeModal}
        footer={null}
        maskClosable={false}
      >
        <IdpInfoForm
          form={idpForm}
          spMetadataURL={getSPMetadataURL(tenantId)}
          modalOnClose={closeModal}
          onFinish={idpFormOnFinish}
          isLoading={isLoading}
          editMode={!!idpMetadataInfoList?.[0]?.idpMetadataId}
          savedDomain={savedDomain}
        />
      </Modal>

      {emailVerificationEnabled && challenge.kind === 'challenging' && effects && (
        <ConfirmOwnershipModal
          key={`domain-otp-${challenge.domain}`}
          open
          email={challenge.adminEmail}
          otpId={challenge.otpId}
          codeIssuedAtMs={challenge.issuedAtMs}
          effects={effects}
          translationRoot="portal.settings.integrations.domain-otp"
          onResent={(otpId, issuedAtMs) =>
            setChallenge(c => (c.kind === 'challenging' ? { ...c, otpId, issuedAtMs } : c))
          }
          onVerified={() => void handleVerified()}
          onCancel={() =>
            setChallenge(c => (c.kind === 'challenging' ? { ...c, kind: 'deferred' } : c))
          }
        />
      )}
    </div>
  );
};

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(SamlIdp);
